import type { ExerciseSpec, Score, ScoreNote } from "./types";

/**
 * The sight-reading generator.
 *
 * This is the reason the course is worth opening tomorrow. A book of pieces can
 * only ever be sight-read once — the second time through, the student is
 * recalling, not reading. So the course generates material that has never
 * existed before, graded to the same difficulty as the piece being studied.
 *
 * The hard part is not randomness, it is *musicality*: random notes inside a
 * scale are unreadable noise and teach nothing. The rules below are the ones
 * that make a generated line feel like something a composer wrote:
 *
 *  - Motion is predominantly stepwise. Leaps are rationed, and a leap is
 *    resolved by step in the opposite direction, which is what real melodies do.
 *  - The line has a shape: it is built as an arc with one high point rather than
 *    a drift, so the student's eye learns to read contour.
 *  - It begins and ends on a stable degree of the key — tonic, mediant or
 *    dominant — so the ear can confirm what the eye read.
 *  - Rhythm is generated per beat, never across beats, so beams group the way a
 *    reader expects and nothing needs a tie to be legible.
 *  - Repeated pitches are limited, because a bar of one note is not reading
 *    practice.
 *
 * The output is a real `Score`, so a generated exercise engraves and plays
 * through exactly the same components as a piece from the book.
 */

/** Mulberry32 — small, fast, and identical across browsers for a given seed. */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const MAJOR_STEPS = [0, 2, 4, 5, 7, 9, 11];
const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
const FLAT_ORDER = ["B", "E", "A", "D", "G", "C", "F"];
const SHARP_TONIC = [0, 7, 2, 9, 4, 11, 6, 1];
const FLAT_TONIC = [0, 5, 10, 3, 8, 1, 6, 11];
const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

const DUR_NAME: Record<number, ScoreNote["d"]> = {
  4: "whole", 2: "half", 1: "quarter", 0.5: "eighth", 0.25: "16th",
};

/** Pitch classes of the key, and which letters carry an accidental. */
function keyContext(fifths: number) {
  const tonicPc = fifths >= 0 ? SHARP_TONIC[fifths] : FLAT_TONIC[-fifths];
  const accidentals = new Map<string, number>();
  if (fifths > 0) SHARP_ORDER.slice(0, fifths).forEach((l) => accidentals.set(l, 1));
  if (fifths < 0) FLAT_ORDER.slice(0, -fifths).forEach((l) => accidentals.set(l, -1));
  return { tonicPc, accidentals };
}

/**
 * Spell a MIDI note using the key's own letters, so F# major reads E# and not F,
 * and Eb major reads Ab and not G#. Written spelling is part of what is being
 * taught, so it has to be right.
 */
function spell(midi: number, fifths: number): string {
  const { accidentals } = keyContext(fifths);
  const pc = ((midi % 12) + 12) % 12;
  for (const letter of LETTERS) {
    const alter = accidentals.get(letter) ?? 0;
    const letterPc = (LETTER_PC[letter] + alter + 12) % 12;
    if (letterPc === pc) {
      // Octave is named from C, and an accidental can push the letter across a
      // C boundary: Cb4 sounds as B3 but is still written in octave 4.
      const naturalMidi = midi - alter;
      const octave = Math.floor(naturalMidi / 12) - 1;
      const acc = alter === 1 ? "#" : alter === -1 ? "b" : "";
      return `${letter}${acc}${octave}`;
    }
  }
  // Not a scale letter (a chromatic note): fall back to a sharp or flat spelling
  // that matches the key's general leaning.
  const octave = Math.floor(midi / 12) - 1;
  const useFlats = fifths < 0;
  const names = useFlats
    ? ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"]
    : ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  return `${names[pc]}${octave}`;
}

/** Every diatonic MIDI note of the key inside the given range, ascending. */
function scaleNotes(fifths: number, range: [number, number]): number[] {
  const { tonicPc } = keyContext(fifths);
  const pcs = new Set(MAJOR_STEPS.map((s) => (tonicPc + s) % 12));
  const out: number[] = [];
  for (let m = range[0]; m <= range[1]; m++) {
    if (pcs.has(((m % 12) + 12) % 12)) out.push(m);
  }
  return out;
}

/**
 * Rhythm for one beat, chosen from the allowed durations.
 *
 * Generated per beat and never across one, so `Beam.generateBeams` groups it the
 * way a reader expects and no tie is ever needed to make a bar legible.
 */
function beatRhythm(beatLen: number, allowed: number[], rand: () => number): number[] {
  const usable = allowed.filter((d) => d <= beatLen).sort((a, b) => b - a);
  if (!usable.length) return [beatLen];
  // Prefer filling the beat with one value; occasionally subdivide it.
  const whole = usable.find((d) => Math.abs(d - beatLen) < 1e-9);
  if (whole && rand() < 0.45) return [whole];
  const out: number[] = [];
  let left = beatLen;
  let guard = 0;
  while (left > 1e-9 && guard++ < 16) {
    const options = usable.filter((d) => d <= left + 1e-9);
    if (!options.length) break;
    const pick = options[Math.floor(rand() * options.length)];
    out.push(pick);
    left -= pick;
  }
  if (left > 1e-9) out.push(left);
  return out.length ? out : [beatLen];
}

export function generateExercise(spec: ExerciseSpec): Score {
  const seed = spec.seed ?? Math.floor(Math.random() * 2 ** 31);
  const rand = rng(seed);

  const scale = scaleNotes(spec.keyFifths, spec.range);
  if (scale.length < 5) {
    throw new Error("range is too narrow to generate a readable exercise");
  }
  const { tonicPc } = keyContext(spec.keyFifths);
  const stable = scale.filter((m) => {
    const deg = (((m % 12) + 12) % 12 - tonicPc + 12) % 12;
    return deg === 0 || deg === 4 || deg === 7;
  });

  const [beats, beatType] = spec.time;
  const beatLen = 4 / beatType;
  const barQuarters = beats * beatLen;

  // ---- shape: an arc with a single high point ------------------------------
  // Reading practice should train contour, so the line is planned before any
  // note is chosen: a target index into the scale for each beat, arcing up to a
  // peak somewhere in the middle and returning.
  const totalBeats = spec.bars * beats;
  const startIdx = scale.indexOf(stable[Math.floor(rand() * stable.length)] ?? scale[0]);
  const endIdx = scale.indexOf(stable[Math.floor(rand() * stable.length)] ?? scale[0]);
  const reach = 2 + Math.floor(rand() * (3 + spec.leapiness * 2));
  const peakAt = 0.35 + rand() * 0.4;
  const peakIdx = Math.min(scale.length - 1,
    Math.max(startIdx, endIdx) + reach);

  const plan: number[] = [];
  for (let b = 0; b < totalBeats; b++) {
    const t = totalBeats === 1 ? 0 : b / (totalBeats - 1);
    const up = t <= peakAt ? (peakAt === 0 ? 1 : t / peakAt) : 1;
    const down = t <= peakAt ? 0 : (t - peakAt) / Math.max(1e-6, 1 - peakAt);
    const rise = startIdx + (peakIdx - startIdx) * easeInOut(up);
    const fall = peakIdx + (endIdx - peakIdx) * easeInOut(down);
    plan.push(t <= peakAt ? rise : fall);
  }

  // ---- notes ---------------------------------------------------------------
  const notes: ScoreNote[] = [];
  let cursorIdx = startIdx;
  let lastLeapDir = 0;
  let sameCount = 0;
  let t = 0;

  for (let bar = 1; bar <= spec.bars; bar++) {
    for (let beatInBar = 0; beatInBar < beats; beatInBar++) {
      const globalBeat = (bar - 1) * beats + beatInBar;
      const rhythm = beatRhythm(beatLen, spec.durations, rand);

      for (let ri = 0; ri < rhythm.length; ri++) {
        const dur = rhythm[ri];
        const isDownbeat = beatInBar === 0 && ri === 0;
        const lastNote = bar === spec.bars && beatInBar === beats - 1
          && ri === rhythm.length - 1;

        // A rest is allowed off the downbeat, and never as the final note.
        if (!isDownbeat && !lastNote && rand() < spec.restChance) {
          notes.push({
            bar, hand: "rh", start: round(t), dur: round(dur),
            d: DUR_NAME[dur] ?? "quarter", rest: true,
          });
          t += dur;
          continue;
        }

        const wanted = plan[Math.min(globalBeat, plan.length - 1)];
        let nextIdx = chooseIndex({
          cursorIdx, wanted, scale, rand,
          leapiness: spec.leapiness, lastLeapDir, sameCount,
          forceStable: lastNote,
          stableIdx: stable.map((m) => scale.indexOf(m)),
        });

        if (nextIdx === cursorIdx) sameCount++;
        else sameCount = 0;
        const jump = nextIdx - cursorIdx;
        lastLeapDir = Math.abs(jump) >= 2 ? Math.sign(jump) : 0;
        cursorIdx = nextIdx;

        const midi = scale[cursorIdx];
        notes.push({
          bar, hand: "rh", start: round(t), dur: round(dur),
          d: DUR_NAME[dur] ?? "quarter",
          pitch: spell(midi, spec.keyFifths), midi,
        });
        t += dur;
      }
    }
  }

  const midis = notes.filter((n) => n.midi).map((n) => n.midi as number);
  return {
    slug: `exercise-${seed}`,
    n: 0,
    title: "Sight-reading exercise",
    composer: "Generated",
    rights: "original",
    publicSafe: true,
    key: keyName(spec.keyFifths),
    fifths: spec.keyFifths,
    time: spec.time,
    tempo: 76,
    staves: 1,
    clefs: [spec.clef],
    bars: spec.bars,
    barQuarters,
    lowest: Math.min(...midis),
    highest: Math.max(...midis),
    hasLyrics: false,
    jasonNote: "",
    teaches: [],
    notes,
  };
}

function easeInOut(x: number) {
  const c = Math.max(0, Math.min(1, x));
  return c < 0.5 ? 2 * c * c : 1 - 2 * (1 - c) * (1 - c);
}

function round(v: number) {
  return Math.round(v * 10000) / 10000;
}

interface ChooseArgs {
  cursorIdx: number;
  wanted: number;
  scale: number[];
  rand: () => number;
  leapiness: number;
  lastLeapDir: number;
  sameCount: number;
  forceStable: boolean;
  stableIdx: number[];
}

/**
 * Pick the next scale index: follow the planned contour, prefer steps, ration
 * leaps, resolve a leap by step against its direction, and refuse to sit on one
 * pitch.
 */
function chooseIndex(a: ChooseArgs): number {
  const { cursorIdx, wanted, scale, rand, leapiness, lastLeapDir, sameCount } = a;

  if (a.forceStable) {
    // Land on the nearest stable degree so the ear can confirm the key.
    let best = a.stableIdx[0] ?? cursorIdx;
    for (const s of a.stableIdx) {
      if (Math.abs(s - cursorIdx) < Math.abs(best - cursorIdx)) best = s;
    }
    return best;
  }

  const maxStep = 1 + (rand() < 0.30 ? leapiness : 0);
  const drift = wanted - cursorIdx;
  let dir = Math.sign(drift) || (rand() < 0.5 ? 1 : -1);

  // A leap wants a step back the other way; that is what makes a line singable.
  if (lastLeapDir !== 0 && rand() < 0.75) dir = -lastLeapDir;
  // Never a third repeat of the same pitch.
  const magnitude = sameCount >= 1
    ? 1
    : Math.min(maxStep, 1 + Math.floor(rand() * maxStep));

  let next = cursorIdx + dir * magnitude;
  if (next < 0 || next > scale.length - 1) next = cursorIdx - dir * magnitude;
  return Math.max(0, Math.min(scale.length - 1, next));
}

function keyName(fifths: number): string {
  const sharp = ["C", "G", "D", "A", "E", "B", "F#", "C#"];
  const flat = ["C", "F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb"];
  return fifths >= 0 ? sharp[Math.min(fifths, 7)] : flat[Math.min(-fifths, 7)];
}

/**
 * A spec graded to match a piece from the book, so the exercise a student is
 * offered is as hard as the piece they are studying and no harder.
 */
export function specForScore(score: Score, seed?: number): ExerciseSpec {
  const durations = uniqueDurations(score);
  const span = score.highest - score.lowest;
  return {
    keyFifths: score.fifths,
    time: score.time,
    bars: 4,
    clef: (score.clefs[0] as "treble" | "bass") ?? "treble",
    // Keep inside the piece's own tessitura, widened slightly so the exercise is
    // not a transposition of the piece itself.
    range: [score.lowest - 1, score.lowest + Math.max(7, span) + 1],
    durations: durations.length ? durations : [1, 2],
    leapiness: span > 12 ? 2 : span > 7 ? 1 : 0,
    restChance: score.notes.some((n) => n.rest) ? 0.12 : 0.05,
    seed,
  };
}

function uniqueDurations(score: Score): number[] {
  const set = new Set<number>();
  for (const n of score.notes) {
    // Only offer plain values; a generated bar should not need dots or ties.
    if ([4, 2, 1, 0.5, 0.25].includes(n.dur)) set.add(n.dur);
  }
  return [...set].sort((a, b) => b - a);
}
